import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { calendarViewScope, hasPermission } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { isoDate } from "./MiniMonth";

import { WEEKDAY_KEY, SLOT_MIN, DAY_OFF_MODES, colorForAppt } from "./calendar/constants";
import { pad, baliMinutes, baliIsoFromLocal, parseHM } from "./calendar/timeUtils";
import { printTransportationSlip } from "./calendar/transportationPrint";
import { openReceipt } from "@/lib/receipt";
import CalendarSidebar from "./calendar/CalendarSidebar";
import CalendarSubnav from "./calendar/CalendarSubnav";
import CalendarToolbar from "./calendar/CalendarToolbar";
import DayGrid from "./calendar/DayGrid";
import WeekStaffGrid from "./calendar/WeekStaffGrid";
import FindAppointmentTab from "./calendar/FindAppointmentTab";
import ContextMenu from "./calendar/ContextMenu";
import ViewAppointmentDialog from "./calendar/ViewAppointmentDialog";
import PersonalApptTapMenu from "./calendar/PersonalApptTapMenu";
import MedicalRecordDialog from "./calendar/MedicalRecordDialog";
import AIBriefingModal from "@/components/AIBriefingModal";
import NewAppointmentModal from "./calendar/NewAppointmentModal";
import GroupNameDialog from "./calendar/dialogs/GroupNameDialog";
import MoveConfirmDialog from "./calendar/dialogs/MoveConfirmDialog";
import CancelAppointmentDialog from "./calendar/dialogs/CancelAppointmentDialog";
import DeleteLunchDialog from "./calendar/dialogs/DeleteLunchDialog";
import DepositActionDialog from "./calendar/dialogs/DepositActionDialog";
import PackagePlacementDialog from "./calendar/dialogs/PackagePlacementDialog";
import CheckInDialog from "./orders/CheckInDialog";
import useCalendarZoom from "./calendar/hooks/useCalendarZoom";

export default function BranchCalendar() {
  const { bid } = useParams();
  const { staff } = useAuth();
  const navigate = useNavigate();
  // Persist the selected calendar day in the URL (?date=YYYY-MM-DD) so that
  // deep-links and the "Back to booking calendar" flow from the customer
  // profile page restore not just the branch, but the exact day the operator
  // was viewing. Falls back to today when the URL has no date param.
  const [searchParams, setSearchParams] = useSearchParams();
  const [branch, setBranch] = useState(null);
  const [date, setDateState] = useState(() => {
    const q = searchParams.get("date");
    if (q && /^\d{4}-\d{2}-\d{2}$/.test(q)) {
      const [y, m, d] = q.split("-").map(Number);
      const dt = new Date(y, m - 1, d);
      if (!isNaN(dt.getTime())) return dt;
    }
    return new Date();
  });
  // Wrap setDate so every state change syncs the URL. `replace: true` keeps
  // the browser history clean when the operator flips between days.
  const setDate = (next) => {
    setDateState(next);
    const iso = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}-${String(next.getDate()).padStart(2, "0")}`;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("date", iso);
    setSearchParams(nextParams, { replace: true });
  };
  const [staffList, setStaffList] = useState([]);
  const [appts, setAppts] = useState([]);
  const [services, setServices] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [packages, setPackages] = useState([]);
  const [openingHours, setOpeningHours] = useState({});
  const [closedDates, setClosedDates] = useState([]);
  const [appointmentSettings, setAppointmentSettings] = useState({});
  const [clipboard, setClipboard] = useState([]);
  const [savedOrders, setSavedOrders] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [prefill, setPrefill] = useState({});
  const [tab, setTab] = useState("calendar");
  const [ctx, setCtx] = useState(null);          // right-click context menu state
  // Iter-268 — read-only View Appointment dialog opened by a single
  // left-click / single tap. Long-press or right-click still open the
  // legacy `ctx` context menu below.
  const [viewAppt, setViewAppt] = useState(null);
  // Iter-380 · Missing-Assistant alert — opens the Edit form directly
  // and scrolls to the staff assignment section. Reuses the same
  // linked-vs-solo branching as the View dialog's Edit action so a
  // package / group sibling still hydrates the full wizard state.
  const openEditFocusStaff = useCallback(async (a) => {
    setViewAppt(null);
    if (a?.link_group_id) {
      try {
        const r = await staffApi.get("/appointments", {
          params: { branch_id: bid, link_group_id: a.link_group_id, include_clipboard: true },
        });
        const siblings = (r.data || [])
          .filter((x) => x.status !== "cancelled" && x.status !== "no_show")
          .sort((x, y) => (x.start_iso || "").localeCompare(y.start_iso || ""));
        setPrefill(siblings.length <= 1
          ? { ...a, editing: true, focusStaff: true }
          : { ...a, editing: true, focusStaff: true, linkedSiblings: siblings });
      } catch {
        toast.error("Couldn't load the linked appointments — please refresh.");
        return;
      }
    } else {
      setPrefill({ ...a, editing: true, focusStaff: true });
    }
    setModalOpen(true);
  }, [bid]);
  // Front-Desk Check-In dialog — set to the appointment record when the
  // operator picks "Check-In Guest" from the context menu. We hydrate the
  // full customer via /customers/{cid} so the dialog can decide which
  // delivery tab (Email / WhatsApp / QR) is offered.
  const [checkinCust, setCheckinCust] = useState(null); // stores appt or customer
  // Group Booking — same-day active groups, keyed by the visible calendar
  // date. Cleared on date change and re-fetched. Empty until at least one
  // group is created for the day.
  const [groups, setGroups] = useState([]);
  const [groupNameDialog, setGroupNameDialog] = useState(null); // { appt } | null
  const [groupNameInput, setGroupNameInput] = useState("");
  const [cancelCtx, setCancelCtx] = useState(null); // { appt, reason_id, note }
  const [cancelReasons, setCancelReasons] = useState([]);
  // When cancel_appointment returns 409 deposit_action_required, we open
  // this second dialog to force the operator to pick a reconciliation path
  // (refund vs convert to points). `pending` holds the payload we'll
  // retry with once the choice is made.
  const [depositDialog, setDepositDialog] = useState(null);
    // { orders: [...], total_deposit, pending: { appt, reason_id, note, is_no_show_label } }
  const [clipboardPulse, setClipboardPulse] = useState(false);
  const clipboardCardRef = useRef(null);
  // Package Placement dialog state (Iter-324). Opened when the
  // operator drops a package header from the clipboard back onto the
  // calendar. Cleared on Cancel / successful Confirm.
  const [packagePlacement, setPackagePlacement] = useState(null);
  const [findQuery, setFindQuery] = useState("");
  const [findResults, setFindResults] = useState([]);

  // ---------- Calendar view scope (Personal vs Global) ----------
  //
  // Driven off the Roles matrix (`location.booking_calendar.personal_view`
  // and `.global_view`). Owner always gets both. When exactly one is
  // granted, the calendar is locked to that mode. When both are on, the
  // "My schedule / Branch view" toggle appears above the grid.
  const scope = useMemo(() => calendarViewScope(staff), [staff]);
  const [viewScope, setViewScope] = useState(() => scope.defaultMode || "global");
  // Re-sync when the signed-in role changes (e.g. after refreshStaff()).
  useEffect(() => {
    setViewScope((prev) => {
      if (scope.canToggle) return prev; // keep operator's choice
      return scope.defaultMode || "global";
    });
  }, [scope.canToggle, scope.defaultMode]);
  const personalMode = viewScope === "personal";
  // Tap menu (Personal View) — opens with AI Summary + Input Medical Record
  // when a practitioner taps an appointment tile in personal mode.
  const [personalTap, setPersonalTap] = useState(null); // { appt, x, y }
  const [medicalDialog, setMedicalDialog] = useState(null); // appt for the record modal

  // ---------- View mode + zoom ----------
  //
  // "day"        → traditional per-staff columns for a single date (default).
  // "weekStaff"  → 7 date columns for ONE staff member. Clicking a date header
  //                returns to the day view for that date.
  //
  // Zoom lives in a dedicated hook that clamps to 0.5–2× and persists to
  // localStorage. It also derives the pixel sizes we need for row height
  // and column min-width.
  const [viewMode, setViewMode] = useState("day");
  const [weekStaffId, setWeekStaffId] = useState(null);
  const [weekAppts, setWeekAppts] = useState([]);
  const { zoomV, zoomH, bumpZoom, slotPx: SLOT_PX_A, colMinW: COL_MIN_W } = useCalendarZoom();

  // For MiniMonth navigation (defaults to the current month + next)
  const [monthCursor, setMonthCursor] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));

  const load = useCallback(async () => {
    // Jun 2026 — every call is settled INDEPENDENTLY. This used to be a
    // Promise.all, so a single failing side-call (saved orders, groups,
    // clipboard…) rejected the whole load and the day rendered with ZERO
    // tiles even though /appointments had answered perfectly — the
    // reported "all bookings disappeared from this date" with healthy
    // data in the database. The bookings now paint whatever else fails.
    const settled = await Promise.allSettled([
      staffApi.get("/branches"),
      staffApi.get(`/branches/${bid}/staff`, { params: { date: isoDate(date) } }),
      staffApi.get(`/appointments`, { params: { branch_id: bid, date: isoDate(date) } }),
      staffApi.get("/services", { params: { branch_id: bid } }),
      staffApi.get(`/branches/${bid}/rooms`),
      staffApi.get(`/branches/${bid}/settings`),
      staffApi.get(`/branches/${bid}/clipboard`),
      staffApi.get(`/orders`, { params: { branch_id: bid, status: "open", linked_active_only: true } }),
      staffApi.get("/packages", { params: { branch_id: bid } }),
      staffApi.get(`/appointment-groups`, { params: { branch_id: bid, business_date: isoDate(date) } }),
    ]);
    const [b, s, a, sv, rm, st, cb, so, pk, gr] = settled.map((r) =>
      r.status === "fulfilled" ? r.value : null);
    const firstFailure = settled.find((r) => r.status === "rejected");
    if (firstFailure) toast.error(formatApiError(firstFailure.reason));
    try {
      if (b) setBranch(b.data.find((x) => x.id === bid) || null);
      if (s) setStaffList(s.data);
      // Cancelled appointments must NEVER appear on the calendar (the
      // week-per-staff view already filters them client-side; the day
      // view was inheriting the raw list which is why a cancelled tile
      // could linger on the grid until refresh). `no_show` is kept so
      // the operator still sees the slot was reserved.
      // Client-side reconciliation: patch every tile whose id shows
      // up in a group's members with the corresponding group_id, so the
      // Users icon renders on the FIRST paint even if the individual
      // appointment doc was stamped without it. Idempotent.
      const groupsData = gr?.data || [];
      const gidByAid = new Map();
      for (const g of groupsData) {
        for (const m of (g.members || [])) {
          if (m.appointment_id) gidByAid.set(m.appointment_id, g.id);
        }
      }
      if (a) {
        const reconciled = (a.data || [])
          .filter((x) => x.status !== "cancelled")
          .map((appt) => {
            const gid = gidByAid.get(appt.id);
            return gid && appt.group_id !== gid ? { ...appt, group_id: gid } : appt;
          });
        setAppts(reconciled);
      }
      if (sv) setServices(sv.data);
      if (rm) setRooms(rm.data);
      if (st) {
        setAppointmentSettings(st.data?.appointment_settings || {});
        setOpeningHours(st.data?.opening_hours || {});
        setClosedDates(st.data?.closed_dates || []);
      }
      if (cb) setClipboard(cb.data || []);
      if (so) setSavedOrders(so.data || []);
      if (pk) setPackages((pk.data || []).filter((p) => p.active !== false));
      setGroups(groupsData);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [bid, date]);

  useEffect(() => { load(); }, [load]);

  // Cancellation reasons are managed brand-wide (Brand Settings → Cancellation
  // Reasons). Fetch once so the right-click "Cancel Appointment" dropdown is
  // ready. Reasons flagged `is_no_show` keep the slot on the calendar as a
  // greyed-out "No Show"; anything else removes it from the grid.
  useEffect(() => {
    (async () => {
      try {
        const r = await staffApi.get("/cancellation-reasons", { params: { active_only: true } });
        setCancelReasons(r.data || []);
      } catch { /* silent — dialog will show empty state */ }
    })();
  }, []);

  // ---------- Day-window computation ----------
  const isoLocal = isoDate(date);
  const closedToday = (closedDates || []).some((c) => (typeof c === "string" ? c === isoLocal : c.date === isoLocal));
  const wKey = WEEKDAY_KEY[date.getDay()];
  const todayHours = (openingHours && openingHours[wKey]) || null;
  const dayIsOpen = !closedToday && !(todayHours && todayHours.open === false);
  const openMin = todayHours && todayHours.open !== false
    ? parseHM(todayHours.open_time, 9 * 60)
    : 9 * 60;
  const closeMin = todayHours && todayHours.open !== false
    ? parseHM(todayHours.close_time, 21 * 60)
    : 21 * 60;
  const slots = useMemo(() => {
    if (!dayIsOpen) return [];
    const out = [];
    for (let m = openMin; m < closeMin; m += SLOT_MIN) out.push(m);
    return out;
  }, [openMin, closeMin, dayIsOpen]);

  // Alphabetical staff order for the calendar columns only. Uses locale-aware,
  // case-insensitive compare so "dr. Angel" sorts as "Angel" (given name).
  // Off-duty (day off / training) or INACTIVE practitioners are hidden unless
  // they still hold appointments (paid or unpaid) on this day — those columns
  // stay visible (greyed) so bookings never silently vanish and the operator
  // can drag them onto another practitioner.
  const staffForCalendar = useMemo(() => {
    // Iter-347 — Multi-Staff Ghost Column Fix.
    // A treatment that requires 2-4 practitioners (PRP, MesoTherapy, etc.)
    // stores ONE primary in `staff_id` and N assists in `assist_staff_ids`.
    // The staffWithAppts set MUST include every assigned member so the
    // calendar renders a column (and therefore a blocking shadow tile) for
    // every practitioner listed on the record — not just the primary.
    // Otherwise an off-duty / inactive assistant is silently stripped from
    // the grid and their shadow tile disappears (P0 double-booking risk).
    // Applies globally to all 8 branches.
    const staffWithAppts = new Set();
    for (const a of appts) {
      if (a.status === "cancelled") continue;
      if (a.staff_id) staffWithAppts.add(a.staff_id);
      if (Array.isArray(a.assist_staff_ids)) {
        for (const sid of a.assist_staff_ids) {
          if (sid) staffWithAppts.add(sid);
        }
      }
    }
    const base = [...staffList]
      .filter((s) => {
        const day = ((s.schedule || {})[wKey]) || {};
        const isOff = s.active === false || DAY_OFF_MODES.has(day.mode);
        if (!isOff) return true;
        // Off / inactive today — only show if they still have bookings.
        return staffWithAppts.has(s.id);
      })
      .sort((a, b) =>
        (a.full_name || "").localeCompare(b.full_name || "", undefined, { sensitivity: "base" })
      );
    // Jun 2026 · No booking may ever be invisible.
    // A tile only paints inside its practitioner's column, so any
    // appointment whose `staff_id` is missing from `staffList` — or is
    // empty altogether — used to vanish from the day even though the
    // API returned it (reported: a closed group order's bookings were
    // listed on the Order page but absent from the calendar). Append a
    // greyed column for every such practitioner, plus one "Unassigned"
    // column, so the operator always sees the booking and can drag it
    // onto a real column.
    const known = new Set(base.map((s) => s.id));
    const orphanIds = [];
    let hasUnassigned = false;
    for (const a of appts) {
      if (a.status === "cancelled") continue;
      if (!a.staff_id) { hasUnassigned = true; continue; }
      if (!known.has(a.staff_id) && !orphanIds.includes(a.staff_id)) orphanIds.push(a.staff_id);
    }
    const orphanColumns = orphanIds.map((id) => ({
      id,
      full_name: (appts.find((a) => a.staff_id === id) || {}).staff_name || "Not on this roster",
      active: false,
      schedule: {},
      __orphan: true,
    }));
    if (hasUnassigned) {
      orphanColumns.push({ id: "", full_name: "Unassigned", active: false, schedule: {}, __orphan: true });
    }
    // Personal View is a client-side belt on top of the server-side lock:
    // even if the API somehow returned another staff's rows, we still only
    // paint the caller's column here.
    if (personalMode && staff?.id) return base.filter((s) => s.id === staff.id);
    return [...base, ...orphanColumns];
  }, [staffList, appts, wKey, personalMode, staff?.id]);

  // Monday-start week that contains the currently-selected `date` — used by
  // the per-staff week view.
  const weekDates = useMemo(() => {
    const dow = date.getDay(); // 0 Sun … 6 Sat
    const mondayOffset = (dow + 6) % 7;
    const start = new Date(date);
    start.setDate(date.getDate() - mondayOffset);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [date]);

  // Fetch appointments across the whole week when the operator clicks an
  // employee name. 7 parallel single-day queries, filtered client-side to the
  // chosen staff so we don't have to change the backend.
  useEffect(() => {
    if (viewMode !== "weekStaff" || !weekStaffId) return;
    let cancelled = false;
    (async () => {
      try {
        const rs = await Promise.all(
          weekDates.map((d) => staffApi.get("/appointments", { params: { branch_id: bid, date: isoDate(d) } }))
        );
        if (cancelled) return;
        const flat = rs.flatMap((r) => r.data).filter((a) =>
          (a.staff_id === weekStaffId || (Array.isArray(a.assist_staff_ids) && a.assist_staff_ids.includes(weekStaffId)))
          && a.status !== "cancelled",
        );
        setWeekAppts(flat);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
    return () => { cancelled = true; };
  }, [viewMode, weekStaffId, weekDates, bid]);

  const enterWeekStaffView = (staffId) => {
    setWeekStaffId(staffId);
    setViewMode("weekStaff");
  };
  const exitWeekStaffView = (targetDate) => {
    if (targetDate) setDate(targetDate);
    setViewMode("day");
    setWeekStaffId(null);
    setWeekAppts([]);
  };

  // Utilisation % for the current per-staff week view.
  //  Denominator: sum of the staff's scheduled working minutes across the week,
  //               net of lunch and branch closures.
  //  Numerator:   sum of `duration_min` for that staff's non-cancelled bookings.
  //  Rendered as a pill on the week title so the operator sees, at a glance,
  //  how "full" a therapist's calendar is.
  const weekUtilisation = useMemo(() => {
    if (viewMode !== "weekStaff" || !weekStaffId) return null;
    const staff = staffList.find((s) => s.id === weekStaffId);
    if (!staff) return null;
    const localIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const closedIsoSet = new Set(
      (closedDates || []).map((c) => (typeof c === "string" ? c : c.date))
    );
    let availableMin = 0;
    for (const d of weekDates) {
      const iso = localIso(d);
      if (closedIsoSet.has(iso)) continue;
      const key = WEEKDAY_KEY[d.getDay()];
      const bh = (openingHours && openingHours[key]) || null;
      if (bh && bh.open === false) continue;
      const bOpen = parseHM(bh?.open_time, 9 * 60);
      const bClose = parseHM(bh?.close_time, 21 * 60);
      const sched = (staff.schedule || {})[key] || {};
      // Empty/missing schedule for that weekday = day off (matches server-side rules).
      if (!sched.work_start && !sched.work_end) continue;
      if (DAY_OFF_MODES.has(sched.mode)) continue;
      const ws = parseHM(sched.work_start, bOpen);
      const we = parseHM(sched.work_end, bClose);
      if (we <= ws) continue;
      let mins = we - ws;
      const ls = parseHM(sched.lunch_start, null);
      const le = parseHM(sched.lunch_end, null);
      if (ls != null && le != null && le > ls) mins -= (le - ls);
      availableMin += Math.max(0, mins);
    }
    const bookedMin = weekAppts.reduce((sum, a) => sum + (a.duration_min || 0), 0);
    if (availableMin === 0) return { booked: bookedMin, available: 0, pct: null };
    return { booked: bookedMin, available: availableMin, pct: Math.round((bookedMin / availableMin) * 100) };
  }, [viewMode, weekStaffId, staffList, weekDates, weekAppts, openingHours, closedDates]);

  const shiftDay = (n) => { const d = new Date(date); d.setDate(d.getDate() + n); setDate(d); };
  const shiftMonth = (n) => {
    setMonthCursor((mc) => new Date(mc.getFullYear(), mc.getMonth() + n, 1));
  };

  const staffAvailableAt = useCallback((staff, minute) => {
    // returns true when the staff's schedule for this weekday covers `minute` (accounting for lunch).
    if (staff.active === false) return false; // deactivated — no new bookings
    const day = ((staff.schedule || {})[wKey]) || {};
    if (DAY_OFF_MODES.has(day.mode)) return false;
    const ws = parseHM(day.work_start, openMin);
    const we = parseHM(day.work_end, closeMin);
    if (minute < ws || minute >= we) return false;
    const ls = parseHM(day.lunch_start, null);
    const le = parseHM(day.lunch_end, null);
    if (ls != null && le != null && minute >= ls && minute < le) return false;
    return true;
  }, [wKey, openMin, closeMin]);

  const openNewApptFromCell = (staffId, minute) => {
    if (!dayIsOpen) return;
    // Booking Calendar · Create gate — silently ignore taps when the
    // signed-in role doesn't hold the create tick. The backend refuses
    // the POST anyway; short-circuiting here avoids the modal flash.
    if (!hasPermission(staff, "location.booking_calendar.create")) {
      toast.error("You don't have permission to create appointments.");
      return;
    }
    const iso = baliIsoFromLocal(date, minute);
    setPrefill({ staff_id: staffId, start_iso: iso });
    setModalOpen(true);
  };

  // ---------- Context menu actions ----------
  const closeCtx = () => setCtx(null);
  // AI Briefing modal — driven either from the appointment context menu or
  // from any other surface that wants to pop up a one-glance clinician sheet.
  const [briefing, setBriefing] = useState(null); // { customerId, customerName, serviceId, serviceName } | null
  useEffect(() => {
    if (!ctx) return;
    const h = () => closeCtx();
    window.addEventListener("click", h);
    window.addEventListener("scroll", h, true);
    return () => { window.removeEventListener("click", h); window.removeEventListener("scroll", h, true); };
  }, [ctx]);

  // Live-sync (Feb 2026): CheckInDialog broadcasts a window event the
  // instant a guest completes their front-desk check-in on the kiosk
  // or their phone. Whenever it fires we refetch the calendar so the
  // teal Checked-In tile paints without a manual page reload. Only
  // reacts while the calendar view is mounted (event listeners are
  // torn down on unmount).
  useEffect(() => {
    const onCustomerUpdated = () => { try { load(); } catch { /* noop */ } };
    // Iter-260 — same-tab schedule save fallback: fires from the
    // BranchScheduleEditor immediately, before the WS round-trip.
    const onScheduleUpdated = (ev) => {
      const detail = ev?.detail;
      if (!detail || detail.branch_id === bid) {
        try { load(); } catch { /* noop */ }
      }
    };
    window.addEventListener("cocoon:customer-updated", onCustomerUpdated);
    window.addEventListener("cocoon:schedule-updated", onScheduleUpdated);
    return () => {
      window.removeEventListener("cocoon:customer-updated", onCustomerUpdated);
      window.removeEventListener("cocoon:schedule-updated", onScheduleUpdated);
    };
  }, [load, bid]);

  // ---- Real-time WebSocket sync (Feb 2026) ------------------------------
  // A single hub-and-spoke WS keeps every calendar mounted anywhere in
  // the brand synced: appointment mutations, customer check-ins, and
  // presence (someone else is drafting on this slot) all fan out over
  // the same channel. Debounced refetch avoids thrashing when many
  // events land in quick succession (e.g. a package wizard POSTing 4
  // siblings back-to-back).
  const [remotePresence, setRemotePresence] = useState([]);
  const refreshTimer = useRef(null);
  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current) return;
    refreshTimer.current = setTimeout(() => {
      refreshTimer.current = null;
      try { load(); } catch { /* noop */ }
    }, 180);
  }, [load]);
  useEffect(() => {
    // Late import to avoid pulling the WS bundle into unrelated pages.
    // eslint-disable-next-line global-require
    const live = require("@/lib/liveSync");
    live.ensureOpen();
    const offAppt = live.subscribe("appointment_updated", () => scheduleRefresh());
    const offCust = live.subscribe("customer_updated", () => scheduleRefresh());
    const offOrder = live.subscribe("order_updated", () => scheduleRefresh());
    // Iter-260 — schedule mutations (regular pattern + weekly
    // override) fan out over the same hub so the calendar repaints a
    // newly-on-duty column the instant an admin flips Sat/Sun from
    // "day off" to "workday" in the schedule editor. Filter to the
    // branch on-screen so unrelated branches don't cause needless
    // refetches.
    const offSched = live.subscribe("schedule_updated", (p) => {
      if (!p || p.branch_id === bid) scheduleRefresh();
    });
    const offPresence = live.subscribe("presence_state", (p) => {
      const list = Array.isArray(p?.presence) ? p.presence : [];
      const selfConn = live.getSelfConnectionId();
      // Hide our own claim from ourselves — we already see the modal.
      setRemotePresence(list.filter((x) => x.connection_id !== selfConn));
    });
    return () => { offAppt(); offCust(); offOrder(); offSched(); offPresence(); };
  }, [scheduleRefresh]);

  // Presence entries that apply to the currently-viewed day + branch.
  // Filter out our own connection so we don't paint a square on top of
  // the operator's own modal.
  const localPresence = useMemo(() => {
    const isoDay = isoDate(date);
    return remotePresence.filter((p) => {
      if (p.branch_id !== bid) return false;
      // Match by Bali-local day so timezone drift never hides a claim.
      try {
        const d = new Date(p.start_iso);
        const dayStr = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Makassar", year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
        return dayStr === isoDay;
      } catch { return false; }
    });
  }, [remotePresence, bid, date]);

  const doMoveToClipboard = async (appt) => {
    try {
      await staffApi.post(`/appointments/${appt.id}/clipboard`, {});
      toast.success("Moved to clipboard — see the panel on the left");
      // Pulse + scroll the clipboard card into view so the operator can find it.
      setClipboardPulse(true);
      setTimeout(() => setClipboardPulse(false), 2200);
      setTimeout(() => {
        try { clipboardCardRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); } catch { /* noop */ }
      }, 80);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doLockToStaff = async (appt) => {
    try {
      await staffApi.post(`/appointments/${appt.id}/lock`, { locked: !appt.locked_to_staff });
      toast.success(appt.locked_to_staff ? "Unlocked" : "Locked to staff");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doAddToGroup = async (appt) => {
    const existing = window.prompt("Enter group id to join (leave blank to start a new group):", appt.group_id || "");
    if (existing === null) return;
    try {
      const r = await staffApi.post(`/appointments/${appt.id}/group`, { group_id: existing || null });
      toast.success(`Group id: ${r.data.group_id}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ---------- Group Booking handlers ----------
  const openCreateGroupDialog = (appt) => {
    setGroupNameInput("");
    setGroupNameDialog({ appt });
  };
  const submitCreateGroup = async () => {
    const name = (groupNameInput || "").trim();
    if (!name || !groupNameDialog) return;
    try {
      await staffApi.post(`/appointment-groups`, {
        appointment_id: groupNameDialog.appt.id,
        name,
      });
      toast.success(`Group "${name}" created`);
      setGroupNameDialog(null);
      setGroupNameInput("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doJoinGroup = async (appt, gid) => {
    try {
      await staffApi.post(`/appointment-groups/${gid}/add`, { appointment_id: appt.id });
      toast.success("Added to group");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doSetPrimary = async (appt) => {
    const gid = appt.group_id;
    if (!gid) return;
    try {
      await staffApi.post(`/appointment-groups/${gid}/primary`, { appointment_id: appt.id });
      toast.success("Primary Payer updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doRemoveFromGroup = async (appt) => {
    const gid = appt.group_id;
    if (!gid) return;
    const group = (groups || []).find((g) => g.id === gid);
    const isPrimary = group && group.primary_appointment_id === appt.id;
    if (isPrimary && !window.confirm("Removing the Primary Payer will split the group and void any open consolidated bill. Continue?")) return;
    try {
      const r = await staffApi.post(`/appointment-groups/${gid}/remove`, { appointment_id: appt.id });
      if (r.data?.status === "split") toast.success("Group split — each member is now solo");
      else toast.success("Removed from group");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doDissolveGroup = async (appt) => {
    const gid = appt.group_id;
    if (!gid) return;
    if (!window.confirm("Dissolve this group? Each member reverts to a solo booking; any open consolidated bill is voided.")) return;
    try {
      await staffApi.delete(`/appointment-groups/${gid}`);
      toast.success("Group dissolved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doTakePaymentGroup = async (appt) => {
    const gid = appt.group_id;
    if (!gid) return doTakePayment(appt);
    // Guardrail: refuse to open the consolidated bill until an actual
    // registered Primary Payer is on the group. Walk-ins don't have a
    // wallet, loyalty ledger, or purchase history to attach the
    // consolidated spend to.
    const grp = (groups || []).find((g) => g.id === gid);
    if (!grp || !grp.primary_customer_id) {
      toast.error("Please select a Primary Payer first — the group has no registered customer set as payer.");
      return;
    }
    try {
      const r = await staffApi.post(`/appointment-groups/${gid}/order`);
      const oid = r.data?.order_id;
      const mergedNums = r.data?.merged_from_order_numbers || [];
      if (mergedNums.length) toast.success(`Group order created — merged invoice(s): ${mergedNums.join(", ")}`);
      if (oid) navigate(`/staff/branch/${bid}/orders?order=${oid}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doLink = async (appt, otherId) => {
    // `otherId` — the appointment to link with. When omitted (manual-id
    // fallback in the submenu), fall back to the classic prompt so
    // operators can still paste an id from another day/branch.
    const other = otherId || window.prompt("Enter the appointment ID of the same customer to link with:");
    if (!other) return;
    try {
      const r = await staffApi.post(`/appointments/${appt.id}/link`, { other_appointment_id: other });
      if (r.data?.order_id) toast.success("Linked — service added to the existing invoice.");
      else toast.success(`Linked (group: ${r.data.link_group_id})`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doUnlink = async (appt) => {
    try { await staffApi.delete(`/appointments/${appt.id}/link`); toast.success("Unlinked"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doCancel = (appt) => {
    // Iter-254 · Transportation tiles are hard-deleted instantly with
    // carry no linked order or profile that would need reconciliation.
    if (appt.is_transportation) {
      const ok = typeof window !== "undefined"
        ? window.confirm("Delete this transportation booking? This cannot be undone.")
        : true;
      if (!ok) return;
      staffApi.delete(`/appointments/${appt.id}`, { params: { hard: true, silent: true } })
        .then(() => { toast.success("Transportation deleted"); load(); })
        .catch((e) => toast.error(formatApiError(e)));
      return;
    }
    // Open the cancellation dialog. Cancellation reasons come from Brand
    // Settings → Cancellation Reasons and are shown as a proper dropdown (no
    // free-text). A reason flagged `is_no_show` keeps the appointment visible
    // on the calendar as "No Show"; anything else removes it from the grid.
    setCancelCtx({ appt, reason_id: "", note: "" });
  };
  const doMarkNoShow = (appt) => {
    // Mark-as-No-Show flow. Opens the same cancellation dialog but with
    // reasons pre-filtered to `is_no_show` only. Confirming sends
    // `deposit_action: "keep"` so any deposit on the linked invoice
    // stays put and the order remains Open — the manager decides later
    // whether to refund it or keep it as a No-Show fee.
    setCancelCtx({ appt, reason_id: "", note: "", only_no_show: true });
  };
  const doRevertNoShow = async (appt) => {
    // Reverse a no-show flag when the guest actually arrived (late). Restores
    // the appointment to `booked` so the front desk can proceed with check-out.
    try {
      await staffApi.post(`/appointments/${appt.id}/revert-no-show`);
      toast.success("No-show reverted · guest is back on the calendar");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const confirmCancel = async () => {
    if (!cancelCtx) return;
    const { appt, reason_id, note, only_no_show } = cancelCtx;
    if (!reason_id) {
      toast.error(only_no_show ? "Please choose a no-show reason" : "Please choose a cancellation reason");
      return;
    }
    try {
      // No-Show flow: send `deposit_action: "keep"` so the backend
      // deposit gate accepts the request without prompting for refund /
      // convert-to-points. The linked order stays Open with its
      // deposit intact — matches the "keep as fee" mental model.
      const payload = { reason_id, note };
      if (only_no_show) payload.deposit_action = "keep";
      await staffApi.delete(`/appointments/${appt.id}`, { data: payload });
      const reason = cancelReasons.find((r) => r.id === reason_id);
      toast.success(reason?.is_no_show ? "Marked as No Show" : "Appointment cancelled");
      setCancelCtx(null);
      load();
    } catch (e) {
      // Intercept the deposit-alert gate — the backend rejects the
      // cancel with HTTP 409 + { code: "deposit_action_required" }
      // when a linked invoice already has money on it. Open the
      // deposit dialog so the operator can pick Refund vs Move to
      // Points, then retry.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "deposit_action_required") {
        // Close the reason picker AlertDialog before opening the deposit
        // reconciliation dialog — keeping both modals mounted at the same
        // time triggers a ResizeObserver focus-measurement loop in the
        // Radix stack that surfaces as the CRA runtime overlay.
        setCancelCtx(null);
        setDepositDialog({
          orders: detail.orders || [],
          total_deposit: detail.total_deposit || 0,
          can_convert: detail.can_convert !== false,
          // Feb 2026 (Job ORD-4NZFMT): backend classifies OPEN+partial
          // as "deposit" and COMPLETED+full as "refund". Frontend
          // renders the right title/copy accordingly.
          dialog: detail.dialog || "deposit",
          title: detail.title,
          message: detail.message,
          pending: { appt, reason_id, note },
        });
      } else {
        toast.error(formatApiError(e));
      }
    }
  };
  const applyDepositAction = async (action) => {
    if (!depositDialog?.pending) return;
    const { appt, reason_id, note } = depositDialog.pending;
    try {
      const res = await staffApi.delete(`/appointments/${appt.id}`, {
        data: { reason_id, note, deposit_action: action },
      });
      const reason = cancelReasons.find((r) => r.id === reason_id);
      const rec = res?.data?.deposit_reconciliation || [];
      const total = rec.reduce((s, r) => s + (r.amount || 0), 0);
      if (action === "convert_to_points") {
        toast.success(`Deposit converted to ${Math.round(total)} points · ${reason?.is_no_show ? "no-show" : "cancelled"}`);
      } else {
        toast.success(`Deposit refunded · ${reason?.is_no_show ? "no-show" : "cancelled"}`);
      }
      setDepositDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const doTakePayment = (appt) => {
    navigate(`/staff/branch/${bid}/orders?appt=${appt.id}`);
  };
  const doTakeDeposit = (appt) => {
    // Deposit mode uses a distinct query flag so BranchOrders knows to
    // auto-print the deposit receipt on every payment save (calendar
    // right-click "Take Deposit" is instant). The regular Take
    // Payment / Checkout flow suppresses auto-print until Close Order.
    navigate(`/staff/branch/${bid}/orders?appt=${appt.id}&mode=deposit`);
  };
  // Iter-255 · Mark a Transportation tile as "picked_up" — a pure
  // operational status. No order close, no financial audit, no
  // deposit refund. Just a PATCH that turns the tile dark grey.
  const doMarkPickedUp = (appt) => {
    if (!appt?.is_transportation) return;
    staffApi.patch(`/appointments/${appt.id}`, { status: "picked_up" }, { params: { branch_id: bid } })
      .then(() => { toast.success("Marked as Picked Up"); load(); })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const doPrint = (appt) => {
    // Iter-254 · Transportation tiles render the dedicated A4 driver
    // with Day/Date/Time incl. treatment time, address, phone) instead
    // of the plain receipt.
    if (appt.is_transportation) {
      const meta = appt.transport_meta || {};
      printTransportationSlip({
        guestName: meta.full_name || appt.customer_name || "Guest",
        hotel: meta.hotel || "",
        address: meta.address || "",
        phone: meta.phone || appt.customer_phone || "",
        startIso: appt.start_iso,
        // Iter-… · The old "(Treatment at HH.MM)" suffix is gone; the slip
        // now carries a Notes line the team types in the appointment's
        // Notes field (or leaves blank to write on by hand).
        notes: appt.notes || meta.notes || "",
      });
      return;
    }
    // Jun 2026 — Print Invoice must produce the EXACT same document as the
    // Order/Bill page. Both surfaces now call `openReceipt(orderId)` (the
    // single `receipt.py::render_receipt_html` renderer); the old ad-hoc
    // <pre> slip printed from here is gone. The context menu only offers
    // this on appointments whose linked invoice is closed/paid, so the
    // guard below is just belt-and-braces for the dialog path.
    const oid = appt.order_id || (appts.find((x) => x.id === appt.id) || {}).order_id;
    if (!oid) {
      toast.error("No closed invoice is linked to this appointment yet.");
      return;
    }
    openReceipt(oid);
  };

  // ---------- Drag & drop ----------
  //
  // Data flavours used on the DataTransfer:
  //   text/appointment-id   → dragged from an existing time slot or from clipboard
  //   text/lunch            → dragged from a lunch block (payload: staff_id|old_minute)
  //
  // Dropping an appointment onto a *different* cell opens a confirmation dialog so
  // the operator can't accidentally reshuffle bookings while scrolling. Locked
  // appointments are blocked from a cross-staff drop at both the client and the
  // backend (see PATCH /appointments guard).
  //
  // Drop coordinates are computed against the day-grid ref (not the individual cell
  // buttons) so a drop at exactly HH:00 lands on that minute — previous behaviour
  // relied on the browser's hit-tested cell which could slip into the row below
  // when the cursor hovered near a slot border.
  const [dropConfirm, setDropConfirm] = useState(null); // { appt, staff_id, minute } | null
  const [dragActive, setDragActive] = useState(null);   // { kind:'appt'|'lunch', service_id?, staff_id?, source_id? }
  const gridRef = useRef(null);
  // Reference to the *body* of the day grid (excluding the sticky header row).
  // Drop math is computed against this so a drop at HH:00 lands on that exact
  // minute — previously the sticky header offset pushed each drop down by ~30 px,
  // which manifested as "drag from 13:15 up to 13:00 lands on 13:30".
  const bodyRef = useRef(null);

  const computeDropTarget = (e) => {
    const body = bodyRef.current;
    const grid = gridRef.current;
    if (!body || !grid) return null;
    const bodyRect = body.getBoundingClientRect();
    // Anchor the drop to the TOP EDGE of the dragged tile, not the mouse
    // pointer. Clipboard drops (and grid-to-grid drops that set their own
    // drag image) publish the pointer-to-tile-top offset via a custom
    // `text/tile-offset-y` DataTransfer entry — subtract it out here so a
    // 60-min tile dropped visually at 1:00 PM snaps to 1:00 PM, not to
    // 1:15 PM (the classic +15-min mismatch reported by operators).
    const rawOffset = e.dataTransfer.getData("text/tile-offset-y");
    const tileOffsetY = rawOffset ? parseFloat(rawOffset) : 0;
    const y = e.clientY - bodyRect.top - (Number.isFinite(tileOffsetY) ? tileOffsetY : 0);
    // Resolve the staff column by hit-testing the ACTUAL rendered staff
    // header buttons — each has a min-width of `COL_MIN_W` so the inner
    // grid overflows horizontally when there are many staff. Dividing the
    // outer gridRect width by staff count silently produces the wrong
    // column under horizontal scroll. Reading the DOM rects is O(N) and
    // fires only on drop, so cost is negligible.
    const cx = e.clientX;
    const headers = grid.querySelectorAll('[data-testid^="staff-col-header-"]');
    let staffId = null;
    for (const h of headers) {
      const r = h.getBoundingClientRect();
      if (cx >= r.left && cx < r.right) {
        // The testid suffix is the staff id (`staff-col-header-<id>`).
        staffId = h.getAttribute("data-testid").replace(/^staff-col-header-/, "");
        break;
      }
    }
    if (!staffId) return null;
    // Snap DOWN to the nearest 15-min slot: Math.floor on `y / SLOT_PX_A`
    // returns the slot whose TOP is at or above the tile's top edge, so a
    // tile that lands anywhere in the 1:00–1:15 band resolves to 1:00 PM.
    // Clamp to 0 so a tile whose top edge lands above the first row (e.g.
    // dragged from the sidebar and dropped near the header) doesn't
    // produce a negative slot index.
    const slotIndex = Math.max(0, Math.floor(y / SLOT_PX_A));
    const minute = openMin + slotIndex * SLOT_MIN;
    return { staffId, minute };
  };

  const onGridDragOver = (e) => { e.preventDefault(); };

  // Grey-net helper — a staff column is greyed while dragging when they can't
  // perform the dragged service (see task 1 in the operator's spec).
  const staffCanPerformService = (staff, serviceId) => {
    if (!staff || !serviceId) return true;
    if (!Array.isArray(staff.service_ids)) return true;
    return staff.service_ids.includes(serviceId);
  };

  const onGridDrop = (e) => {
    e.preventDefault();
    const target = computeDropTarget(e);
    setDragActive(null);
    if (!target) return;
    const { staffId, minute } = target;

    const lunchPayload = e.dataTransfer.getData("text/lunch");
    if (lunchPayload) {
      const [origStaffId, origMinute] = lunchPayload.split("|");
      if (origStaffId !== staffId) {
        toast.error("Lunch can only be moved within the same staff column.");
        return;
      }
      moveLunch(origStaffId, parseInt(origMinute, 10), minute);
      return;
    }

    // Bulk clipboard package drop (Iter-324) — open the placement
    // dialog so the operator can assign a start time + care team to
    // every sub-service before the atomic write fires.
    const dropPackageId = e.dataTransfer.getData("text/package-id");
    const dropPackageCustId = e.dataTransfer.getData("text/package-customer-id");
    if (dropPackageId) {
      const items = clipboard.filter((c) =>
        c.package_id === dropPackageId && c.customer_id === dropPackageCustId
      );
      if (!items.length) {
        toast.error("Package is no longer on the clipboard — please refresh.");
        return;
      }
      setPackagePlacement({
        packageId: dropPackageId,
        customerId: dropPackageCustId,
        packageName: (packages.find((p) => p.id === dropPackageId) || {}).name || "Package",
        items,
        dropMinute: minute,
        dropStaffId: staffId,
      });
      return;
    }

    const aid = e.dataTransfer.getData("text/appointment-id");
    if (!aid) return;
    const existing = appts.find((a) => a.id === aid);
    // Iter-350 · Role-Aware Drag Split.
    // DayGrid publishes which column the tile was dragged FROM plus the
    // pre-computed role ("primary" vs "assist"). Legacy drag paths (week
    // view, clipboard) don't set these entries — fall back to "primary"
    // so existing behaviour is preserved byte-for-byte.
    const draggedFromStaffId = e.dataTransfer.getData("text/dragged-column-staff-id") || null;
    const draggedRoleRaw = e.dataTransfer.getData("text/dragged-role") || null;
    if (existing) {
      const originStaffId = draggedFromStaffId || existing.staff_id;
      const isAssistDrag = draggedRoleRaw === "assist"
        || (draggedFromStaffId
            && draggedFromStaffId !== existing.staff_id
            && Array.isArray(existing.assist_staff_ids)
            && existing.assist_staff_ids.includes(draggedFromStaffId));
      const sameCol = staffId === originStaffId;
      const sameMinute = baliMinutes(existing.start_iso) === minute;
      if (sameCol && sameMinute) return;

      if (isAssistDrag) {
        // Assistant tile — Primary staff_id is STRICTLY LOCKED. A sideways
        // drag swaps the old assistant for the new one; a vertical drag
        // reschedules the whole appointment (primary + every assist) via
        // start_iso only. Dropping an assist onto the primary's column
        // would duplicate the primary — refuse.
        if (!sameCol && staffId === existing.staff_id) {
          toast.error("Cannot drop an assistant onto the primary practitioner's column.");
          return;
        }
        if (!sameCol) {
          const canDo = staffCanPerformService(staffList.find((s) => s.id === staffId), existing.service_id);
          if (!canDo) {
            toast.error("This team member is not trained to perform this service.");
            return;
          }
          // Refuse to double-add the same assist if the target is already on
          // the team (would be a no-op after dedup).
          if (Array.isArray(existing.assist_staff_ids) && existing.assist_staff_ids.includes(staffId)) {
            toast.error("This team member is already an assistant on this appointment.");
            return;
          }
        }
        setDropConfirm({
          appt: existing,
          staff_id: staffId,
          minute,
          role: "assist",
          dragged_from_staff_id: originStaffId,
        });
        return;
      }

      // Primary tile — legacy behaviour: staff swap and/or time move.
      if (existing.locked_to_staff && existing.staff_id !== staffId) {
        toast.error("This appointment is locked to its assigned staff. Right-click to unlock first.");
        return;
      }
      // Guard the client too: don't offer to drop onto a staff who can't perform
      // the service. Backend still enforces via /availability + service_ids.
      const canDo = staffCanPerformService(staffList.find((s) => s.id === staffId), existing.service_id);
      if (!canDo) {
        toast.error("This team member is not trained to perform this service.");
        return;
      }
      setDropConfirm({ appt: existing, staff_id: staffId, minute, role: "primary" });
      return;
    }
    // Not on the current day yet — must be a clipboard drop.
    // Look up the clipboard entry so we can guard against untrained-staff drops
    // client-side (mirrors the on-grid drag behavior + the grey-net overlay).
    const clipEntry = clipboard.find((c) => c.id === aid);
    if (clipEntry) {
      const canDo = staffCanPerformService(staffList.find((s) => s.id === staffId), clipEntry.service_id);
      if (!canDo) {
        toast.error("This team member is not trained to perform this service.");
        return;
      }
    }
    (async () => {
      const doUnclip = async (allowDoubleBook = false) => {
        await staffApi.post(`/appointments/${aid}/unclip`, {
          start_iso: baliIsoFromLocal(date, minute),
          staff_id: staffId,
          allow_double_book: allowDoubleBook,
        });
      };
      try {
        await doUnclip(false);
        toast.success("Dropped onto calendar");
        load();
      } catch (err) {
        // Iter-325 · Clipboard drop follows the same rules as the
        // on-grid drag: a 409 slot conflict prompts the operator for
        // a double-book override instead of hard-rejecting.
        const status = err?.response?.status;
        const detail = ((err?.response?.data?.detail) || "").toString().toLowerCase();
        const isConflict = status === 409 && (
          detail.includes("selected time is not available") ||
          detail.includes("already booked")
        );
        if (isConflict) {
          const proceed = window.confirm(
            "This team member already has a booking at this time.\n\nDrop anyway (double-book)?"
          );
          if (!proceed) return;
          try {
            await doUnclip(true);
            toast.success("Dropped onto calendar (double-booked)");
            load();
          } catch (err2) { toast.error(formatApiError(err2)); }
          return;
        }
        toast.error(formatApiError(err));
      }
    })();
  };

  const confirmMove = async () => {
    if (!dropConfirm) return;
    const { appt, staff_id, minute, role, dragged_from_staff_id } = dropConfirm;
    // Iter-350 · Role-Aware PATCH builder.
    // Primary drag → include `staff_id` (may swap the Primary practitioner)
    //   + `start_iso` (time move). Legacy behaviour preserved.
    // Assist  drag → NEVER include `staff_id` (Primary is LOCKED). A
    //   sideways move rewrites `assist_staff_ids` to swap the old assistant
    //   for the new one; a vertical move only carries `start_iso` so every
    //   assigned practitioner (primary + assists) is rescheduled together.
    const doPatch = async (extra = {}) => {
      const body = { start_iso: baliIsoFromLocal(date, minute), ...extra };
      if (role === "assist") {
        const isSideways = staff_id !== dragged_from_staff_id;
        if (isSideways) {
          const current = Array.isArray(appt.assist_staff_ids) ? appt.assist_staff_ids : [];
          const swapped = current
            .filter((sid) => sid && sid !== dragged_from_staff_id)
            .concat(staff_id);
          // Dedup + defensively strip the Primary so a payload sanity check
          // never fires backend-side.
          body.assist_staff_ids = Array.from(new Set(swapped.filter((sid) => sid !== appt.staff_id)));
        }
        // Vertical-only assist drag → time move applies to the whole
        // appointment; no staff_id / assist_staff_ids in the payload.
      } else {
        body.staff_id = staff_id;
      }
      await staffApi.patch(`/appointments/${appt.id}`, body, { params: { branch_id: bid } });
    };
    try {
      await doPatch();
      if (role === "assist" && staff_id !== dragged_from_staff_id) {
        toast.success("Assistant reassigned");
      } else {
        toast.success("Appointment moved");
      }
      setDropConfirm(null);
      load();
    } catch (err) {
      // 409 → the target staff is already booked in that slot. Offer the
      // operator a "Book anyway?" override that mirrors the double-book
      // confirm on New Appointment. The dialog is dismissed regardless of
      // outcome so the operator sees the exact reason (toast) if they
      // decline or the second attempt still fails for a different reason.
      const status = err?.response?.status;
      const detail = ((err?.response?.data?.detail) || "").toString().toLowerCase();
      const isConflict = status === 409 && (
        detail.includes("selected time is not available") ||
        detail.includes("already booked")
      );
      if (isConflict) {
        setDropConfirm(null);
        const proceed = window.confirm(
          "This team member is already booked at this time.\n\nMove anyway (double-book)?"
        );
        if (!proceed) return;
        try {
          await doPatch({ allow_double_book: true });
          toast.success("Appointment moved (double-booked)");
          load();
        } catch (err2) {
          toast.error(formatApiError(err2));
        }
        return;
      }
      toast.error(formatApiError(err));
      setDropConfirm(null);
    }
  };

  // ---------- Lunch move / delete ----------
  const moveLunch = async (staffId, oldMinute, newMinute) => {
    const staff = staffList.find((s) => s.id === staffId);
    if (!staff) return;
    const daySchedule = (staff.schedule || {})[wKey] || {};
    const ls = parseHM(daySchedule.lunch_start, null);
    const le = parseHM(daySchedule.lunch_end, null);
    if (ls == null || le == null) return;
    const duration = le - ls;
    const newStart = newMinute;
    const newEnd = newStart + duration;
    // Keep the lunch inside the working window so the schedule stays consistent.
    const ws = parseHM(daySchedule.work_start, openMin);
    const we = parseHM(daySchedule.work_end, closeMin);
    if (newStart < ws || newEnd > we) {
      toast.error("Lunch must stay within the working hours.");
      return;
    }
    const newLunchStart = `${pad(Math.floor(newStart / 60))}:${pad(newStart % 60)}`;
    const newLunchEnd = `${pad(Math.floor(newEnd / 60))}:${pad(newEnd % 60)}`;
    const dateIso = isoDate(date);
    // OPTIMISTIC: snap the tile to the new slot immediately so the
    // operator sees the drag land the moment they release the mouse.
    // On success we OVERWRITE with the exact `day` the SERVER stored
    // (the "newest instance data" contract) so the tile never drifts
    // from what's actually in Mongo. On failure we roll the state
    // back and surface the error.
    const priorStaffList = staffList;
    setStaffList((prev) => prev.map((s) => (
      s.id === staffId
        ? { ...s, schedule: { ...(s.schedule || {}), [wKey]: { ...daySchedule, lunch_start: newLunchStart, lunch_end: newLunchEnd } } }
        : s
    )));
    try {
      // Instance-scoped PATCH — ONLY moves this single date, does NOT
      // touch the recurring rule. Server response carries the merged
      // day payload we redraw from.
      const resp = await staffApi.patch(
        `/branches/${bid}/staff-users/${staffId}/lunch-instance`,
        { date: dateIso, lunch_start: newLunchStart, lunch_end: newLunchEnd },
      );
      const serverDay = resp?.data?.day || null;
      if (serverDay) {
        setStaffList((prev) => prev.map((s) => (
          s.id === staffId ? { ...s, schedule: { ...(s.schedule || {}), [wKey]: serverDay } } : s
        )));
      }
      // Toast fires AFTER the setState so the operator never sees a
      // "success" message while the tile is still stuck at the old
      // position.
      toast.success("Lunch moved");
      // Background reconcile — picks up cross-branch conflict flags
      // that only surface after a save. Optimistic patch keeps the
      // tile pinned during the fetch window.
      load();
    } catch (err) {
      setStaffList(priorStaffList);
      toast.error(formatApiError(err));
    }
  };

  const [lunchDelete, setLunchDelete] = useState(null); // staffId | null
  const requestDeleteLunch = (staffId) => setLunchDelete(staffId);
  const confirmDeleteLunch = async () => {
    const staffId = lunchDelete;
    if (!staffId) return;
    const staff = staffList.find((s) => s.id === staffId);
    if (!staff) { setLunchDelete(null); return; }
    const daySchedule = (staff.schedule || {})[wKey] || {};
    const priorStaffList = staffList;
    setStaffList((prev) => prev.map((s) => (
      s.id === staffId
        ? { ...s, schedule: { ...(s.schedule || {}), [wKey]: { ...daySchedule, lunch_start: "", lunch_end: "" } } }
        : s
    )));
    try {
      const resp = await staffApi.patch(
        `/branches/${bid}/staff-users/${staffId}/lunch-instance`,
        { date: isoDate(date), lunch_start: "", lunch_end: "" },
      );
      const serverDay = resp?.data?.day || null;
      if (serverDay) {
        setStaffList((prev) => prev.map((s) => (
          s.id === staffId ? { ...s, schedule: { ...(s.schedule || {}), [wKey]: serverDay } } : s
        )));
      }
      toast.success("Lunch removed");
      setLunchDelete(null);
      load();
    } catch (err) {
      setStaffList(priorStaffList);
      toast.error(formatApiError(err));
      setLunchDelete(null);
    }
  };

  // ---------- Appointment bottom-edge resize ----------
  // Native mouse events (not HTML5 DnD) so the operator can drag the bottom of a
  // booking to shorten or lengthen the service time. Snaps to 15-min increments
  // and PATCHes duration_min once the mouse releases.
  const [resizePreview, setResizePreview] = useState({}); // { [aid]: durationMin }
  const onResizeStart = (e, appt) => {
    e.stopPropagation();
    e.preventDefault();
    const startY = e.clientY;
    const startDur = appt.duration_min || 30;
    const onMove = (ev) => {
      const dy = ev.clientY - startY;
      const deltaSlots = Math.round(dy / SLOT_PX_A);
      const next = Math.max(15, startDur + deltaSlots * SLOT_MIN);
      setResizePreview((rp) => ({ ...rp, [appt.id]: next }));
    };
    const onUp = async (ev) => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const dy = ev.clientY - startY;
      const deltaSlots = Math.round(dy / SLOT_PX_A);
      const next = Math.max(15, startDur + deltaSlots * SLOT_MIN);
      setResizePreview((rp) => { const cp = { ...rp }; delete cp[appt.id]; return cp; });
      if (next === startDur) return;
      try {
        await staffApi.patch(`/appointments/${appt.id}`, { duration_min: next }, { params: { branch_id: bid } });
        toast.success(`Duration set to ${next} min`);
        load();
      } catch (err) {
        const status = err?.response?.status;
        const detail = ((err?.response?.data?.detail) || "").toString().toLowerCase();
        // Extending a booking so it overlaps another one → offer double-book override.
        if (status === 409 && detail.includes("overlaps another booking")) {
          const proceed = window.confirm(
            "The new duration overlaps another booking on this staff.\n\nResize anyway (double-book)?"
          );
          if (!proceed) return;
          try {
            await staffApi.patch(`/appointments/${appt.id}`, { duration_min: next, allow_double_book: true }, { params: { branch_id: bid } });
            toast.success(`Duration set to ${next} min (double-booked)`);
            load();
          } catch (err2) { toast.error(formatApiError(err2)); }
          return;
        }
        toast.error(formatApiError(err));
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // ---------- Find appointment ----------
  useEffect(() => {
    if (tab !== "find" || !findQuery.trim()) { setFindResults([]); return; }
    const t = setTimeout(async () => {
      try {
        // fetch customers matching + their upcoming appointments
        const cust = await staffApi.get(`/customers`, { params: { search: findQuery.trim() } });
        const ids = (cust.data || []).map((c) => c.id);
        const perCustomer = await Promise.all(
          ids.slice(0, 20).map((id) => staffApi.get(`/appointments`, { params: { customer_id: id } }).then((r) => r.data))
        );
        const flat = perCustomer.flat().sort((a, b) => b.start_iso.localeCompare(a.start_iso)).slice(0, 40);
        setFindResults(flat);
      } catch { /* silent */ }
    }, 300);
    return () => clearTimeout(t);
  }, [findQuery, tab]);

  const selectedSavedOrder = (v) => { if (v) navigate(`/staff/branch/${bid}/orders?order=${v}`); };
  const goToDate = (d) => { setDate(d); setTab("calendar"); };

  return (
    <StaffShell branchName={branch?.name} context="branch">
      <CalendarSubnav
        tab={tab}
        setTab={setTab}
        savedOrders={savedOrders}
        onSelectSavedOrder={selectedSavedOrder}
      />

      {tab === "find" ? (
        <FindAppointmentTab query={findQuery} setQuery={setFindQuery} results={findResults} onOpenDate={goToDate} />
      ) : (
        <div className="grid grid-cols-[180px_1fr] gap-4">
          <CalendarSidebar
            monthCursor={monthCursor}
            isoLocal={isoLocal}
            onSelectDate={setDate}
            onShiftMonth={shiftMonth}
            clipboardCardRef={clipboardCardRef}
            clipboardPulse={clipboardPulse}
            clipboard={clipboard}
            setDragActive={setDragActive}
            packages={packages}
          />

          {/* Main day / week grid */}
          <div className="min-w-0">
            <CalendarToolbar
              viewMode={viewMode}
              date={date}
              setDate={setDate}
              shiftDay={shiftDay}
              weekDates={weekDates}
              weekStaffId={weekStaffId}
              staffList={staffList}
              weekUtilisation={weekUtilisation}
              onExitWeekStaff={() => exitWeekStaffView()}
              scope={scope}
              personalMode={personalMode}
              setViewScope={setViewScope}
              zoomV={zoomV}
              zoomH={zoomH}
              bumpZoom={bumpZoom}
            />

            <div className="bg-white rounded-lg border border-border overflow-auto">
              {viewMode === "weekStaff" ? (
                <WeekStaffGrid
                  weekDates={weekDates}
                  slots={slots}
                  weekAppts={weekAppts}
                  onSelectDate={(d) => exitWeekStaffView(d)}
                  onCellClick={(d, minute) => {
                    setPrefill({ staff_id: weekStaffId, start_iso: baliIsoFromLocal(d, minute) });
                    setModalOpen(true);
                  }}
                  colMinW={COL_MIN_W}
                  slotPx={SLOT_PX_A}
                  resizePreview={resizePreview}
                  colorForAppt={colorForAppt}
                  packages={packages}
                  services={services}
                  onApptClick={(a) => setViewAppt(a)}
                  onApptContextMenu={(e, a) => { e.preventDefault(); setCtx({ appt: a, x: e.clientX, y: e.clientY }); }}
                  onApptAssignAssistant={(a) => openEditFocusStaff(a)}
                />
              ) : !dayIsOpen ? (
                <div className="p-6 text-center text-sm text-muted-foreground" data-testid="cal-closed-banner">
                  {closedToday ? "Branch is closed on this date." : "Branch is closed on this weekday."}
                </div>
              ) : personalMode && (staffForCalendar.length === 0 || (staff?.role === "brand_admin") || !(staff?.service_ids && staff.service_ids.length)) ? (
                // Personal View is on but the signed-in user isn't a
                // practitioner (brand admin / front-desk / no assigned
                // services) — the backend falls back to returning their
                // own doc, but there's nothing useful to render. Nudge
                // them to Branch view instead of a blank column.
                <div className="p-8 text-center text-sm text-muted-foreground space-y-3" data-testid="cal-personal-empty-state">
                  <p>You&apos;re viewing <b>My schedule</b>, but you aren&apos;t assigned as a practitioner at this location.</p>
                  {scope.canToggle && (
                    <button
                      type="button"
                      onClick={() => setViewScope("global")}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-border text-xs hover:bg-muted"
                      data-testid="cal-personal-empty-switch-global"
                    >
                      Switch to Branch view
                    </button>
                  )}
                </div>
              ) : staffForCalendar.length === 0 ? (
                // Off-Duty Column Hide (2026-02): the whole roster for
                // this date is on Day Off / Training / Closed and nobody
                // holds a lingering appointment. Show a clean empty
                // state instead of a blank grid so the operator
                // realises they're not seeing a rendering bug.
                <div className="p-8 text-center text-sm text-muted-foreground" data-testid="cal-no-staff-empty-state">
                  <p>No staff on duty for this date.</p>
                </div>
              ) : (
                <DayGrid
                  staffForCalendar={staffForCalendar}
                  slots={slots}
                  colMinW={COL_MIN_W}
                  slotPx={SLOT_PX_A}
                  appts={appts}
                  resizePreview={resizePreview}
                  packages={packages}
                  services={services}
                  wKey={wKey}
                  date={isoDate(date)}
                  dragActive={dragActive}
                  setDragActive={setDragActive}
                  staffCanPerformService={staffCanPerformService}
                  gridRef={gridRef}
                  bodyRef={bodyRef}
                  onEnterWeekStaff={enterWeekStaffView}
                  onCellClick={openNewApptFromCell}
                  onGridDragOver={onGridDragOver}
                  onGridDrop={onGridDrop}
                  onLunchDragStart={(staffId) => setDragActive({ kind: "lunch", staff_id: staffId })}
                  onLunchDelete={requestDeleteLunch}
                  onApptClick={(a) => {
                    if (personalMode) setPersonalTap({ appt: a, x: 0, y: 0 });
                    else setViewAppt(a);
                  }}
                  onApptContextMenu={(e, a) => {
                    e.preventDefault(); e.stopPropagation();
                    if (personalMode) {
                      setPersonalTap({ appt: a, x: e.clientX, y: e.clientY });
                    } else {
                      setCtx({ appt: a, x: e.clientX, y: e.clientY });
                    }
                  }}
                  onApptResizeStart={onResizeStart}
                  onApptAssignAssistant={(a) => openEditFocusStaff(a)}
                  staffAvailableAt={staffAvailableAt}
                  personalMode={personalMode}
                  remotePresence={localPresence}
                  openMin={openMin}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {ctx && (
        <ContextMenu
          x={ctx.x} y={ctx.y}
          appt={ctx.appt}
          onClose={closeCtx}
          onCheckInGuest={() => { setCheckinCust(ctx.appt); closeCtx(); }}
          onTakePayment={() => { doTakePayment(ctx.appt); closeCtx(); }}
          onTakePaymentGroup={() => { doTakePaymentGroup(ctx.appt); closeCtx(); }}
          onTakePaymentSolo={() => { doTakePayment(ctx.appt); closeCtx(); }}
          onTakeDeposit={() => { doTakeDeposit(ctx.appt); closeCtx(); }}
          onCancel={() => { doCancel(ctx.appt); closeCtx(); }}
          onMarkPickedUp={() => { doMarkPickedUp(ctx.appt); closeCtx(); }}
          onMarkNoShow={() => { doMarkNoShow(ctx.appt); closeCtx(); }}
          onRevertNoShow={() => { doRevertNoShow(ctx.appt); closeCtx(); }}
          onLock={() => { doLockToStaff(ctx.appt); closeCtx(); }}
          onClipboard={() => { doMoveToClipboard(ctx.appt); closeCtx(); }}
          currentGroup={ctx.appt.group_id ? (groups || []).find((g) => g.id === ctx.appt.group_id) || null : null}
          activeGroups={groups || []}
          packageName={ctx.appt.package_id ? ((packages || []).find((p) => p.id === ctx.appt.package_id) || {}).name || null : null}
          onCreateGroup={() => { openCreateGroupDialog(ctx.appt); closeCtx(); }}
          onAddToGroup={(gid) => { doJoinGroup(ctx.appt, gid); closeCtx(); }}
          onSetPrimaryPayer={() => { doSetPrimary(ctx.appt); closeCtx(); }}
          onRemoveFromGroup={() => { doRemoveFromGroup(ctx.appt); closeCtx(); }}
          onDissolveGroup={() => { doDissolveGroup(ctx.appt); closeCtx(); }}
          onEdit={async () => {
            const a = ctx.appt;
            closeCtx();
            // Linked visits (package OR ad-hoc "+ Add Another Service") open
            // the whole group so the operator can edit every sibling from a
            // single dialog. Solo appointments open the flat editor.
            if (a.link_group_id) {
              try {
                const r = await staffApi.get("/appointments", {
                  params: { branch_id: bid, link_group_id: a.link_group_id, include_clipboard: true },
                });
                // Removed/cancelled steps must NOT resurface as editable
                // rows — only active siblings belong in the linked editor.
                const siblings = (r.data || [])
                  .filter((x) => x.status !== "cancelled" && x.status !== "no_show")
                  .sort((x, y) => (x.start_iso || "").localeCompare(y.start_iso || ""));
                if (siblings.length <= 1) {
                  // The group dissolved down to (at most) this appointment —
                  // open the flat solo editor instead of a 1-step wizard.
                  setPrefill({ ...a, editing: true });
                } else {
                  setPrefill({ ...a, editing: true, linkedSiblings: siblings });
                }
              } catch (err) {
                toast.error("Couldn't load the linked appointments — please refresh.");
                return;
              }
            } else {
              setPrefill({ ...a, editing: true });
            }
            setModalOpen(true);
          }}
          onPrint={() => { doPrint(ctx.appt); closeCtx(); }}
          onLink={() => { doLink(ctx.appt); closeCtx(); }}
          onLinkTo={(otherId) => { doLink(ctx.appt, otherId); closeCtx(); }}
          linkCandidates={(() => {
            // Same-client + same-day appts on the visible calendar. `date` is
            // the day currently shown; we compare using the Bali-local date
            // portion of the ISO string so an appointment at 23:30 Bali is
            // grouped with the day the operator sees on the grid.
            const a = ctx.appt;
            if (!a.customer_id) return [];
            const dayKey = new Date(a.start_iso).toLocaleDateString("en-CA", { timeZone: "Asia/Makassar" });
            return appts
              .filter((x) => x.id !== a.id
                && x.customer_id === a.customer_id
                && new Date(x.start_iso).toLocaleDateString("en-CA", { timeZone: "Asia/Makassar" }) === dayKey
                && (!a.link_group_id || x.link_group_id !== a.link_group_id))
              .sort((p, q) => (p.start_iso || "").localeCompare(q.start_iso || ""))
              .map((x) => ({
                id: x.id,
                service_name: x.service_name,
                start_iso: x.start_iso,
                staff_name: (staffList.find((s) => s.id === x.staff_id) || {}).full_name || "",
              }));
          })()}
          onUnlink={() => { doUnlink(ctx.appt); closeCtx(); }}
          onSeeProfile={() => {
            const custId = ctx.appt.customer_id;
            closeCtx();
            if (!custId) return;
            // Pass the current calendar location as state so the profile
            // page can render a "Back to booking calendar" link that lands
            // on the exact branch + day the operator came from.
            navigate(`/staff/brand/customers/${custId}`, {
              state: {
                backTo: {
                  path: `${window.location.pathname}${window.location.search}`,
                  label: "Back to booking calendar",
                },
              },
            });
          }}
          onAIBriefing={() => {
            const a = ctx.appt;
            closeCtx();
            if (!a.customer_id) return;
            setBriefing({
              customerId: a.customer_id,
              customerName: a.customer_name || "Guest",
              serviceId: a.service_id || null,
              serviceName: a.service_name || "",
              appointmentId: a.id || null,
            });
          }}
        />
      )}

      {briefing && (
        <AIBriefingModal
          open={!!briefing}
          onOpenChange={(v) => { if (!v) setBriefing(null); }}
          customerId={briefing.customerId}
          customerName={briefing.customerName}
          serviceId={briefing.serviceId}
          serviceName={briefing.serviceName}
          appointmentId={briefing.appointmentId}
        />
      )}

      {personalTap && (
        <PersonalApptTapMenu
          x={personalTap.x}
          y={personalTap.y}
          appt={personalTap.appt}
          onClose={() => setPersonalTap(null)}
          onAISummary={() => {
            const a = personalTap.appt;
            setPersonalTap(null);
            if (!a?.customer_id) { toast.error("This slot has no linked client."); return; }
            setBriefing({
              customerId: a.customer_id,
              customerName: a.customer_name || "Guest",
              serviceId: a.service_id || null,
              serviceName: a.service_name || "",
              appointmentId: a.id || null,
            });
          }}
          onMedicalRecord={() => {
            const a = personalTap.appt;
            setPersonalTap(null);
            if (!a?.customer_id) { toast.error("This slot has no linked client."); return; }
            setMedicalDialog(a);
          }}
        />
      )}

      {medicalDialog && (
        <MedicalRecordDialog
          open={!!medicalDialog}
          onOpenChange={(v) => { if (!v) setMedicalDialog(null); }}
          appt={medicalDialog}
        />
      )}

      <NewAppointmentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        branchId={bid}
        branch={branch}
        staffList={staffList}
        services={services}
        packages={packages}
        rooms={rooms}
        prefill={prefill}
        onCreated={load}
        appointmentSettings={appointmentSettings}
      />

      {/* Group Booking — name popup shown when the operator picks
          "Create new group…" from the appointment right-click menu. */}
      <GroupNameDialog
        open={!!groupNameDialog}
        appt={groupNameDialog?.appt}
        name={groupNameInput}
        setName={setGroupNameInput}
        onSubmit={submitCreateGroup}
        onClose={() => { setGroupNameDialog(null); setGroupNameInput(""); }}
      />

      {/* Confirm the appointment move triggered by a drag-and-drop. */}
      <MoveConfirmDialog
        dropConfirm={dropConfirm}
        staffList={staffList}
        onConfirm={confirmMove}
        onClose={() => setDropConfirm(null)}
      />

      {/* Cancel Appointment dialog (dropdown of Brand Settings → Cancellation
          Reasons; picking a `is_no_show` reason marks the slot "no_show" and
          keeps it on the grid). */}
      <CancelAppointmentDialog
        cancelCtx={cancelCtx}
        cancelReasons={cancelReasons}
        setCancelCtx={setCancelCtx}
        onConfirm={confirmCancel}
        onClose={() => setCancelCtx(null)}
      />

      <DeleteLunchDialog
        lunchDelete={lunchDelete}
        staffList={staffList}
        date={date}
        onConfirm={confirmDeleteLunch}
        onClose={() => setLunchDelete(null)}
      />

      {/* Deposit reconciliation gate — appears when a cancellation
          targets an appointment whose linked invoice already has money
          on it. Operator picks Refund (revert to source) or Move to
          Points (100% conversion to loyalty). */}
      <DepositActionDialog
        depositDialog={depositDialog}
        onApply={applyDepositAction}
        onClose={() => setDepositDialog(null)}
      />

      {packagePlacement && (
        <PackagePlacementDialog
          open={!!packagePlacement}
          onClose={() => setPackagePlacement(null)}
          packageName={packagePlacement.packageName}
          items={packagePlacement.items}
          staffList={staffList}
          dropMinute={packagePlacement.dropMinute}
          dropStaffId={packagePlacement.dropStaffId}
          onConfirm={async ({ moves }) => {
            const payload = moves.map((m) => {
              const [hh, mm] = (m.time || "").split(":").map((x) => parseInt(x, 10));
              const minute = (hh || 0) * 60 + (mm || 0);
              return {
                appointment_id: m.appointment_id,
                start_iso: baliIsoFromLocal(date, minute),
                staff_id: m.staff_id,
              };
            });
            const doDrop = async (allowDoubleBook = false) => {
              await staffApi.post(`/appointments/package-unclip`, {
                moves: payload,
                allow_double_book: allowDoubleBook,
              });
            };
            try {
              await doDrop(false);
              toast.success("Package placed on the calendar");
              setPackagePlacement(null);
              load();
            } catch (err) {
              // Iter-325 · Same double-book override the solo clipboard
              // drop honours — a 409 on any sub-service prompts once
              // and retries the WHOLE package with the flag set.
              const status = err?.response?.status;
              const detail = ((err?.response?.data?.detail) || "").toString().toLowerCase();
              const isConflict = status === 409 && detail.includes("overlaps");
              if (isConflict) {
                const proceed = window.confirm(
                  "One or more services in this package overlap an existing booking.\n\nPlace anyway (double-book)?"
                );
                if (!proceed) return;
                try {
                  await doDrop(true);
                  toast.success("Package placed on the calendar (double-booked)");
                  setPackagePlacement(null);
                  load();
                } catch (err2) { toast.error(formatApiError(err2)); }
                return;
              }
              toast.error(formatApiError(err));
            }
          }}
        />
      )}

      {/* Iter-268 — View Appointment dialog (single-click read-only view).
          Iter-270 · Remap `viewAppt` to the freshest appointment doc from
          the current `appts` array so a check-in stamp flowing in via
          live-sync updates the dialog's Take-Payment gate WITHOUT the
          operator having to close and re-open the popup. Falls back to
          the captured snapshot when the tile is no longer on the grid
          (e.g. rescheduled to another day) so the dialog can still
          gracefully close via the X. */}
      {viewAppt && (() => {
        const fresh = appts.find((x) => x.id === viewAppt.id) || viewAppt;
        return (
        <ViewAppointmentDialog
          open={!!viewAppt}
          onOpenChange={(v) => { if (!v) setViewAppt(null); }}
          appt={fresh}
          branchName={branch?.name || ""}
          staffName={(staffList.find((s) => s.id === fresh.staff_id) || {}).full_name || ""}
          roomName={(rooms.find((r) => r.id === fresh.room_id) || {}).name || ""}
          onEdit={async () => {
            const a = fresh;
            setViewAppt(null);
            // Reuse the linked-vs-solo branching from the context-menu
            // Edit action so both paths open the same wizard state.
            if (a.link_group_id) {
              try {
                const r = await staffApi.get("/appointments", {
                  params: { branch_id: bid, link_group_id: a.link_group_id, include_clipboard: true },
                });
                const siblings = (r.data || [])
                  .filter((x) => x.status !== "cancelled" && x.status !== "no_show")
                  .sort((x, y) => (x.start_iso || "").localeCompare(y.start_iso || ""));
                setPrefill(siblings.length <= 1
                  ? { ...a, editing: true }
                  : { ...a, editing: true, linkedSiblings: siblings });
              } catch {
                toast.error("Couldn't load the linked appointments — please refresh.");
                return;
              }
            } else {
              setPrefill({ ...a, editing: true });
            }
            setModalOpen(true);
          }}
          onTakePayment={() => { const a = fresh; setViewAppt(null); doTakePayment(a); }}
          onCheckIn={() => { const a = fresh; setViewAppt(null); setCheckinCust(a); }}
          onPrint={() => { doPrint(fresh); }}
        />
        );
      })()}

      {/* Front-Desk Check-In — mounted here so the right-click "Check-In
          Guest" menu item can lift a fresh delivery picker (Email /
          WhatsApp / QR) with the guest's contact context hydrated from
          the appointment tile. The picker mints the token server-side. */}
      <CheckInDialog
        open={!!checkinCust}
        onOpenChange={(v) => { if (!v) setCheckinCust(null); }}
        customer={checkinCust ? {
          id: checkinCust.customer_id,
          first_name: (checkinCust.customer_name || "").split(" ")[0] || "Guest",
          full_name: checkinCust.customer_name,
          email: checkinCust.customer_email,
          whatsapp: checkinCust.customer_whatsapp,
        } : null}
        onSent={load}
        onCompleted={load}
      />
    </StaffShell>
  );
}
